import React, { type ComponentType } from 'react';
import { flushSync } from 'react-dom';
import { createRoot, type Root } from 'react-dom/client';
import { getProps, getChildren } from 'utils/DOM';

type RegisteredComponent = ComponentType<Record<string, unknown>>;

const listeners: { selector: string; Component: RegisteredComponent }[] = [];

// One React root per DOM node. Rendering again on the same node (attribute
// changes) updates the existing root, like the legacy render() did.
const roots = new WeakMap<Element, Root>();

function renderComponent(node: Element, Component: RegisteredComponent) {
  const props = getProps(node);
  props.children = getChildren(node);

  let root = roots.get(node);
  if (!root) {
    root = createRoot(node);
    roots.set(node, root);
  }

  // createRoot renders asynchronously; flush to keep the old synchronous
  // behavior so the DOM is ready as soon as a component is registered.
  flushSync(() => {
    root.render(<Component {...props} container={node} />);
  });
}

function unmountComponent(node: Element) {
  const root = roots.get(node);
  if (root) {
    roots.delete(node);
    root.unmount();
  }
}

function checkAdded(node: Node) {
  const added = node as Element;
  if (['STYLE', '#text', 'SCRIPT'].indexOf(added.nodeName) === -1) {
    const nodeNames = listeners.map(({ selector }) => selector.toLowerCase());
    const nodeName = added.nodeName.toLowerCase();
    listeners.forEach(({ selector, Component }) => {
      try {
        if (
          nodeNames.indexOf(nodeName) !== -1 &&
          nodeName === selector.toLowerCase()
        ) {
          renderComponent(added, Component);
        } else {
          const nodes = added.querySelectorAll(selector);
          Array.prototype.forEach.call(nodes, node => {
            renderComponent(node, Component);
          });
        }
        /* eslint-disable no-empty */
      } catch (e) {}
    });
  }
}

function checkRemoved(node: Node) {
  const removed = node as Element;
  if (['STYLE', '#text', 'SCRIPT'].indexOf(removed.nodeName) === -1) {
    const nodeNames = listeners.map(({ selector }) => selector.toLowerCase());
    const nodeName = removed.nodeName.toLowerCase();
    listeners.forEach(({ selector }) => {
      try {
        if (
          nodeNames.indexOf(nodeName) !== -1 &&
          nodeName === selector.toLowerCase()
        ) {
          unmountComponent(removed);
        } else {
          const nodes = removed.querySelectorAll(selector);
          Array.prototype.forEach.call(nodes, node => {
            unmountComponent(node);
          });
        }
        /* eslint-disable no-empty */
      } catch (e) {}
    });
  }
}

function updateAttributes(target: Node) {
  listeners.forEach(({ selector, Component }) => {
    try {
      if (selector.toLowerCase() === target.nodeName.toLowerCase()) {
        renderComponent(target as Element, Component);
      }
      /* eslint-disable no-empty */
    } catch (e) {}
  });
}

const observer = new MutationObserver(mutationsList => {
  for (const mutation of mutationsList) {
    if (mutation.type == 'childList') {
      if (mutation.removedNodes.length > 0) {
        mutation.removedNodes.forEach(checkRemoved);
      }

      if (mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach(checkAdded);
      }
    } else if (mutation.type == 'attributes') {
      updateAttributes(mutation.target);
    }
  }
});

observer.observe(document, {
  childList: true,
  subtree: true,
  attributes: true,
});

export default <P extends object>(
  selector: string,
  Component: ComponentType<P>
) => {
  // The props come from the page's attributes, which TypeScript cannot check.
  const register = Component as unknown as RegisteredComponent;

  listeners.push({
    selector,
    Component: register,
  });

  try {
    const nodes = document.querySelectorAll(selector);
    Array.prototype.forEach.call(nodes, node => {
      renderComponent(node, register);
    });
    /* eslint-disable no-empty */
  } catch (e) {}
};
